import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { useBooking, BUILDINGS_CONFIG } from '../../../contexts/BookingContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useLanguage } from '../../../contexts/LanguageContext';

// ─── Anomaly Detection Config ─────────────────────────────────────────────────
const CAPACITY_THRESHOLD = 0.85; // 85% occupancy triggers alert
const NO_SHOW_HOURS = 24;        // hours past check-in date = no-show risk
const BURST_CHECKIN_WINDOW = 60; // minutes window for burst detection
const BURST_CHECKIN_COUNT = 3;   // check-ins within window = unusual pattern

const ALERT_TYPES = {
  CAPACITY: 'capacity',
  NO_SHOW: 'no_show',
  BURST: 'burst',
};

const SEVERITY = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

function detectAnomalies(stays, rooms) {
  const alerts = [];
  const now = new Date();
  const todayStr = now?.toISOString()?.split('T')?.[0];

  // ── 1. Capacity Threshold per Building ──────────────────────────────────────
  const buildingKeys = Object.keys(BUILDINGS_CONFIG);
  buildingKeys?.forEach(building => {
    const totalRooms = BUILDINGS_CONFIG?.[building]?.floors?.reduce(
      (acc, f) => acc + (f?.rooms?.length || 0), 0
    );
    const occupiedCount = stays?.filter(
      s => s?.building === building && s?.status === 'confirmed-stay'
    )?.length;
    const reservedCount = stays?.filter(
      s => s?.building === building && s?.status === 'active-reservation'
    )?.length;
    const usedCount = occupiedCount + reservedCount;
    const ratio = totalRooms > 0 ? usedCount / totalRooms : 0;

    if (ratio >= CAPACITY_THRESHOLD) {
      const pct = Math.round(ratio * 100);
      alerts?.push({
        id: `cap-${building}`,
        type: ALERT_TYPES?.CAPACITY,
        severity: ratio >= 0.95 ? SEVERITY?.HIGH : SEVERITY?.MEDIUM,
        building,
        title: `Building ${building} at ${pct}% capacity`,
        message: `${usedCount}/${totalRooms} rooms occupied or reserved. ${pct >= 95 ? 'Critical — no rooms available soon.' : 'Approaching full capacity.'}`,
        icon: 'Building2',
        color: ratio >= 0.95 ? 'rose' : 'amber',
        detectedAt: now?.toISOString(),
        meta: { occupiedCount, reservedCount, totalRooms, pct },
      });
    }
  });

  // ── 2. No-Show Risk ──────────────────────────────────────────────────────────
  stays?.forEach(stay => {
    if (stay?.status !== 'active-reservation') return;
    const checkInDate = new Date(stay?.checkIn);
    const hoursPast = (now - checkInDate) / (1000 * 60 * 60);
    if (hoursPast >= NO_SHOW_HOURS) {
      alerts?.push({
        id: `noshow-${stay?.id}`,
        type: ALERT_TYPES?.NO_SHOW,
        severity: hoursPast >= 48 ? SEVERITY?.HIGH : SEVERITY?.MEDIUM,
        title: `No-show risk: ${stay?.residentName}`,
        message: `Expected check-in on ${stay?.checkIn} — ${Math.floor(hoursPast)}h overdue. Room ${stay?.room} (${stay?.building}) still reserved.`,
        icon: 'UserX',
        color: hoursPast >= 48 ? 'rose' : 'amber',
        detectedAt: now?.toISOString(),
        stayId: stay?.id,
        meta: { residentName: stay?.residentName, room: stay?.room, checkIn: stay?.checkIn, hoursPast: Math.floor(hoursPast) },
      });
    }
  });

  // ── 3. Unusual Burst Check-in Pattern ────────────────────────────────────────
  const recentCheckIns = stays?.filter(s => {
    if (s?.status !== 'confirmed-stay') return false;
    // Use id as proxy timestamp (Date.now() based)
    const stayTime = typeof s?.id === 'number' && s?.id > 1000000000000
      ? new Date(s?.id)
      : null;
    if (!stayTime) return false;
    const minsAgo = (now - stayTime) / (1000 * 60);
    return minsAgo <= BURST_CHECKIN_WINDOW;
  });

  if (recentCheckIns?.length >= BURST_CHECKIN_COUNT) {
    alerts?.push({
      id: `burst-${todayStr}`,
      type: ALERT_TYPES?.BURST,
      severity: SEVERITY?.MEDIUM,
      title: `Unusual check-in burst detected`,
      message: `${recentCheckIns?.length} check-ins recorded in the last ${BURST_CHECKIN_WINDOW} minutes. Verify all registrations are legitimate.`,
      icon: 'Zap',
      color: 'violet',
      detectedAt: now?.toISOString(),
      meta: { count: recentCheckIns?.length, window: BURST_CHECKIN_WINDOW },
    });
  }

  return alerts;
}

// ─── Single Alert Row ─────────────────────────────────────────────────────────
function AlertRow({ alert, onDismiss, onNotifyAdmins, notified }) {
  const colorMap = {
    rose: { bg: 'bg-rose-50 border-rose-200', icon: 'text-rose-500', badge: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
    amber: { bg: 'bg-amber-50 border-amber-200', icon: 'text-amber-500', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
    violet: { bg: 'bg-violet-50 border-violet-200', icon: 'text-violet-500', badge: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500' },
  };
  const c = colorMap?.[alert?.color] || colorMap?.amber;
  const severityLabel = alert?.severity === SEVERITY?.HIGH ? 'High' : alert?.severity === SEVERITY?.MEDIUM ? 'Medium' : 'Low';

  return (
    <div
      className={`flex items-start gap-3 p-3.5 rounded-xl border ${c?.bg} transition-all`}
      style={{ animation: 'alertSlideIn 0.25s cubic-bezier(0.34,1.56,0.64,1) both' }}
    >
      <div className={`mt-0.5 shrink-0 ${c?.icon}`}>
        <Icon name={alert?.icon} size={17} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-foreground leading-tight">{alert?.title}</p>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${c?.badge}`}>{severityLabel}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{alert?.message}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0 ml-1">
        {!notified && (
          <button
            onClick={() => onNotifyAdmins(alert)}
            title="Notify admins"
            className="w-7 h-7 rounded-lg bg-white/70 hover:bg-white border border-current/20 flex items-center justify-center transition-colors"
          >
            <Icon name="Bell" size={13} className={c?.icon} />
          </button>
        )}
        {notified && (
          <span className="w-7 h-7 rounded-lg bg-white/70 border border-current/20 flex items-center justify-center">
            <Icon name="CheckCheck" size={13} className="text-emerald-500" />
          </span>
        )}
        <button
          onClick={() => onDismiss(alert?.id)}
          title="Dismiss"
          className="w-7 h-7 rounded-lg bg-white/70 hover:bg-white border border-current/20 flex items-center justify-center transition-colors text-muted-foreground hover:text-foreground"
        >
          <Icon name="X" size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AnomalyAlertSystem() {
  const { stays, rooms } = useBooking();
  const { notifyAdmins } = useNotifications();
  const { language } = useLanguage();

  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [notified, setNotified] = useState(new Set());
  const [isExpanded, setIsExpanded] = useState(true);
  const [lastScan, setLastScan] = useState(null);
  const intervalRef = useRef(null);

  const runScan = useCallback(() => {
    const detected = detectAnomalies(stays, rooms);
    setAlerts(detected);
    setLastScan(new Date());
  }, [stays, rooms]);

  // Run on mount and whenever stays change
  useEffect(() => {
    runScan();
  }, [runScan]);

  // Periodic re-scan every 2 minutes
  useEffect(() => {
    intervalRef.current = setInterval(runScan, 2 * 60 * 1000);
    return () => clearInterval(intervalRef?.current);
  }, [runScan]);

  const handleDismiss = (alertId) => {
    setDismissed(prev => new Set([...prev, alertId]));
  };

  const handleNotifyAdmins = async (alert) => {
    const typeLabel = {
      [ALERT_TYPES?.CAPACITY]: '🏢 Capacity Alert',
      [ALERT_TYPES?.NO_SHOW]: '⚠️ No-Show Risk',
      [ALERT_TYPES?.BURST]: '⚡ Unusual Activity',
    };
    await notifyAdmins({
      title: typeLabel?.[alert?.type] || '🔔 Anomaly Alert',
      message: alert?.message,
      type: alert?.severity === SEVERITY?.HIGH ? 'error' : 'warning',
      relatedTable: 'stays',
    });
    setNotified(prev => new Set([...prev, alert?.id]));
  };

  const handleNotifyAll = async () => {
    const visibleAlerts = alerts?.filter(a => !dismissed?.has(a?.id) && !notified?.has(a?.id));
    for (const alert of visibleAlerts) {
      await handleNotifyAdmins(alert);
    }
  };

  const visibleAlerts = alerts?.filter(a => !dismissed?.has(a?.id));
  const highCount = visibleAlerts?.filter(a => a?.severity === SEVERITY?.HIGH)?.length;
  const unnotifiedCount = visibleAlerts?.filter(a => !notified?.has(a?.id))?.length;

  if (visibleAlerts?.length === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3">
        <Icon name="ShieldCheck" size={17} className="text-emerald-500 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-emerald-800">
            {language === 'fr' ? 'Aucune anomalie détectée' : 'No anomalies detected'}
          </p>
          <p className="text-xs text-emerald-600 mt-0.5">
            {language === 'fr' ? 'Tous les indicateurs sont normaux.' : 'All indicators are within normal range.'}
            {lastScan && ` · ${language === 'fr' ? 'Dernière analyse' : 'Last scan'}: ${lastScan?.toLocaleTimeString()}`}
          </p>
        </div>
        <button
          onClick={runScan}
          className="w-7 h-7 rounded-lg bg-emerald-100 hover:bg-emerald-200 flex items-center justify-center transition-colors"
          title={language === 'fr' ? 'Relancer l\'analyse' : 'Re-run scan'}
        >
          <Icon name="RefreshCw" size={13} className="text-emerald-600" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer select-none border-b border-border bg-muted/40 hover:bg-muted/60 transition-colors"
        onClick={() => setIsExpanded(v => !v)}
      >
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <Icon name="AlertTriangle" size={17} className={highCount > 0 ? 'text-rose-500' : 'text-amber-500'} />
            {highCount > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
            )}
          </div>
          <span className="text-sm font-semibold text-foreground">
            {language === 'fr' ? 'Alertes d\'anomalies' : 'Anomaly Alerts'}
          </span>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${highCount > 0 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
            {visibleAlerts?.length}
          </span>
          {lastScan && (
            <span className="text-xs text-muted-foreground hidden sm:inline">
              · {language === 'fr' ? 'Mis à jour' : 'Updated'} {lastScan?.toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unnotifiedCount > 0 && isExpanded && (
            <button
              onClick={e => { e?.stopPropagation(); handleNotifyAll(); }}
              className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
            >
              <Icon name="BellRing" size={12} />
              {language === 'fr' ? 'Notifier les admins' : 'Notify Admins'}
            </button>
          )}
          <button
            onClick={e => { e?.stopPropagation(); runScan(); }}
            className="w-7 h-7 rounded-lg bg-muted hover:bg-border flex items-center justify-center transition-colors"
            title={language === 'fr' ? 'Relancer l\'analyse' : 'Re-run scan'}
          >
            <Icon name="RefreshCw" size={13} className="text-muted-foreground" />
          </button>
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={15} className="text-muted-foreground" />
        </div>
      </div>
      {/* Alert List */}
      {isExpanded && (
        <div className="p-3 space-y-2">
          {visibleAlerts?.map(alert => (
            <AlertRow
              key={alert?.id}
              alert={alert}
              onDismiss={handleDismiss}
              onNotifyAdmins={handleNotifyAdmins}
              notified={notified?.has(alert?.id)}
            />
          ))}
        </div>
      )}
      <style>{`
        @keyframes alertSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
